import React, { useState } from "react";
import { ModalX, PageHeader } from "../../components";
import { Button, Checkbox, IconButton, Input, Option, Select, Spinner } from "@material-tailwind/react";
import { Edit2Icon } from "lucide-react";
import moment from "moment";
import { convertToPresentableText, NotifyError } from "../../utils/functions";
import { useGetBuildings } from "../../custom-hooks/use-buildings";
import { useAddFloor, useGetFloors, useUpdateFloor } from "../../custom-hooks/use-floors";
import { InterpretOperationalStatus } from "../../utils/ui-interpreters";
import { OPERATIONAL_STATUSES_AS_ARRAY } from "../../utils/enums";
import { OPERATIONAL_STATUSES_DESCRIPTION } from "../../utils/enums_descriptions";
import { usePermissions } from "../../custom-hooks/use-permissions";


const INITIAL_FORM_DATA = {
  building: '',
  number: '',
  floorCode: '',
  usableArea: '',
  totalArea: '',
  status: '',
  description: '',

  photos: [],
  floorPlan: null,
  facilities: [], // e.g., ["meeting rooms", "restrooms", "lounge", cafeteria, prayer_room, gameroom, reception, public area, smoking area, private area, guard, storage, server room, power room, kitchen]

  meetingRooms: 0,
  restrooms: 0,
  lounge: 0,
  cafeteria: 0,
  prayerRoom: 0,
  gameroom: 0,
  reception: 0,
  publicArea: 0,
  smokingArea: 0,
  privateArea: 0,
  guard: 0,
  emergencyExits: 0,

  fireSafetyStatus: false,
  powerBackup: false,
  internetAvailability: false,
  climateControl: false,
}
const FloorPage = () => {

  // State variables
  // const [branches, setBranches] = useState([]);
  // const [buildings, setBuildings] = useState([]);
  // const [floors, setFloors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedFloorId, setSelectedFloorId] = useState(null);
  // const [googleMapsLinkStatus, setGoogleMapsLinkStatus] = useState('');
  // const [isLoadingCoordinates, setIsLoadingCoordinates] = useState(false);

  const { data: buildings, isLoading: isBuildingsLoading, isError: isBuildingFetchError } = useGetBuildings();
  const { data: floors, isLoading: isFloorsLoading, isError: isFloorsFetchError } = useGetFloors('all', { returnStats: true });

  const addFloorMutation = useAddFloor();
  const updateFloorMutation = useUpdateFloor();

  const { can } = usePermissions();

  // Handle form input change
  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckBoxChange = async (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isUpdating) {
        // Update building logic

        const updatedFloor = {
          building: formData.building,
          number: formData.number,
          floorCode: formData.floorCode,
          usableArea: formData.usableArea,
          totalArea: formData.totalArea,
          status: formData.status,
          description: formData.description,
          photos: formData.photos,
          floorPlan: formData.floorPlan,
          facilities: formData.facilities,
          meetingRooms: formData.meetingRooms,
          restrooms: formData.restrooms,
          lounge: formData.lounge,
          cafeteria: formData.cafeteria,
          prayerRoom: formData.prayerRoom,
          gameroom: formData.gameroom,
          reception: formData.reception,
          publicArea: formData.publicArea,
          smokingArea: formData.smokingArea,
          privateArea: formData.privateArea,
          guard: formData.guard,
          emergencyExits: formData.emergencyExits,
          fireSafetyStatus: formData.fireSafetyStatus,
          powerBackup: formData.powerBackup,
          internetAvailability: formData.internetAvailability,
          climateControl: formData.climateControl,
        };
        await updateFloorMutation.mutateAsync({ floorId: selectedFloorId, data: updatedFloor });

      } else {
        // Add new building logic
        const newFloor = {
          building: formData.building,
          number: formData.number,
          floorCode: formData.floorCode,
          usableArea: formData.usableArea,
          totalArea: formData.totalArea,
          status: formData.status,
          description: formData.description,
          photos: formData.photos,
          floorPlan: formData.floorPlan,
          facilities: formData.facilities,
          meetingRooms: formData.meetingRooms,
          restrooms: formData.restrooms,
          lounge: formData.lounge,
          cafeteria: formData.cafeteria,
          prayerRoom: formData.prayerRoom,
          gameroom: formData.gameroom,
          reception: formData.reception,
          publicArea: formData.publicArea,
          smokingArea: formData.smokingArea,
          privateArea: formData.privateArea,
          guard: formData.guard,
          emergencyExits: formData.emergencyExits,
          fireSafetyStatus: formData.fireSafetyStatus,
          powerBackup: formData.powerBackup,
          internetAvailability: formData.internetAvailability,
          climateControl: formData.climateControl,
        };
        await addFloorMutation.mutateAsync({ buildingId: formData.building, data: newFloor });
      }
    } catch (error) {
      console.log(error);
      NotifyError(error.response.data.message, error);

    } finally {
      setModalOpen(false);
      setFormData(INITIAL_FORM_DATA);
      setIsUpdating(false);
      setSelectedFloorId(null);
    }
  };

  // Open modal for adding a new building
  const openAddFloorModal = () => {
    setModalOpen(true);
    const defaultValueForInitialFormData = { ...INITIAL_FORM_DATA };
    defaultValueForInitialFormData.status = 'operational';

    setFormData(defaultValueForInitialFormData);
    setIsUpdating(false);
  };

  // Open modal for updating a building
  const openUpdateFloorModal = (floor) => {
    setModalOpen(true);
    setFormData({ ...floor, building: floor.building._id });
    setIsUpdating(true);
    setSelectedFloorId(floor._id);
  };

  return (
    <div className={``}>

      <div className="m-5 p-5 border rounded-3xl">
        <PageHeader
          title="Floor Management"
          isLoading={isFloorsLoading}
          actionOnClick={can('FX6', 'edit') ? openAddFloorModal : undefined}
          actionType="add"
          actionText="Add Floor"
        />
        <div className="overflow-x-auto mt-4">
          {isFloorsLoading ? (
            <div className="flex justify-center py-5">
              <Spinner />
            </div>
          ) : isFloorsFetchError ? (
            <div className="text-center py-5 text-red-500">
              Error loading floors. Please try again.
            </div>
          ) : (
            <table className="w-full min-w-max table-auto text-left">
              <thead className=" text-sm">
                <tr className="py-3">
                  <th colSpan={6} className="py-3 text-xs font-light text-orange-900">List of All Floors ({floors?.length || 0})</th>
                  {/* <th colSpan={2} className="py-3">
                  <Input
                    label="Search"
                  />
                </th> */}
                </tr>
                <tr className="py-3 uppercase">
                  <th className="py-3">#</th>
                  <th className="py-3">Offices</th>
                  <th className="py-3">Shared Areas</th>
                  <th className="py-3">Status</th>

                  <th className="py-3">Created</th>
                  {can('FX6', 'edit') && (
                  <th className="py-3">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {floors?.map((floor, index) => (
                  <tr key={index} draggable className="border-t hover:bg-gray-50 button rounded-full">
                    <td className="font-thin py-3 text-sm">
                      <div>
                        <div className="font-bold">
                          {floor.number} ({floor.floorCode})
                        </div>
                        <div className="text-xs text-gray-500">
                          {floor.building.name} - {floor.building.branch.name}
                        </div>
                      </div>
                    </td>
                    <td className="font-thin py-3 text-sm">
                      <div className="">
                        <div className="font-bold">
                          Total {floor.officeCount}
                        </div>
                        <div className="text-xs text-gray-500">
                          {floor.offices?.reduce((total, office) => total + (office.capacity || 0), 0) || 'No'} Capacity
                          {/* Operational: {floor.offices.filter(office => office.status === OPERATIONAL_STATUSES.OPERATIONAL).length} */}
                        </div>
                      </div>
                    </td>
                    <td className="font-thin py-3 text-sm">
                      <div className="">
                        <div className="font-bold">
                          Total {floor.coworkingCount}
                        </div>
                        <div className="text-xs text-gray-500">
                          {floor.coworkings?.reduce((total, coworking) => total + (coworking.seats.length || 0), 0) || 'No'} Seats
                          {/* Operational: {floor.coworking.filter(coworking => coworking.status === OPERATIONAL_STATUSES.OPERATIONAL).reduce((total, coworking) => total + 1, 0)} */}
                        </div>
                      </div>
                    </td>
                    <td className="font-thin py-3 text-sm">
                      <div className="flex">
                        {InterpretOperationalStatus(floor.status)}
                      </div>
                    </td>
                    <td className="font-thin py-3 text-sm">
                      <div className="">
                        <div className="text-xs">
                          C: {moment(floor.createdDate).format('lll')}
                        </div>
                        <div className="text-xs">
                          U: {moment(floor.updatedDate).format('lll')}
                        </div>
                      </div>
                    </td>
                    {can('FX6', 'edit') && (
                    <td className="flex gap-1 py-3">
                      {/* <IconButton
                      size="sm"
                      className="rounded-full"
                      onClick={() => openUpdateFloorModal(floor)}
                    >
                      <EyeIcon size={12} />
                    </IconButton> */}
                      <IconButton
                        size="sm"
                        color="orange"
                        className="rounded-full"
                        onClick={() => openUpdateFloorModal(floor)}
                      >
                        <Edit2Icon size={12} />
                      </IconButton>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>

            </table>)}
        </div>

        {/* Modal for Add/Update Floor */}
        <ModalX isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          title={isUpdating ? "Update Floor" : "Add New Floor"}
          maxWidth="xxl">
          <form onSubmit={handleFormSubmit}>
            <div className="space-y-2">
              {
                isBuildingFetchError && <div className="text-center py-5 text-red-500">Error loading buildings. Please try again.</div>
              }
              <div className="">
                <Select
                  label={<>Select Building <span className="text-red-500 ml-1">*</span></>}
                  value={formData.building}
                  disabled={isBuildingsLoading}
                  onChange={(newValue) => handleSelectChange("building", newValue)}
                >
                  {
                    buildings?.map((building, index) => (
                      <Option key={index} value={building._id}>
                        <div className="flex justify-between gap-3 w-full">
                          <div className="">
                            {building.name} ({building.buildingCode})
                          </div>
                          <div className="">
                            {building.branch.name}
                          </div>
                        </div>
                      </Option>
                    ))
                  }
                </Select>
              </div>
              <div className="flex gap-2 flex-col md:flex-row">
                <Input
                  label="Floor Number"
                  name="number"
                  value={formData.number}
                  onChange={handleInputChange}
                  required
                />
                <Input
                  label="Floor Code"
                  name="floorCode"
                  value={formData.floorCode}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <Select
                label="Status"
                value={formData.status}
                onChange={newValue => handleSelectChange("status", newValue)}
              >
                {
                  OPERATIONAL_STATUSES_AS_ARRAY.map((opStatus, i) => (
                    <Option key={i} value={opStatus.value}>{convertToPresentableText(opStatus.value)} - {OPERATIONAL_STATUSES_DESCRIPTION[opStatus.key]}</Option>
                  ))
                }
              </Select>

              <div className="flex gap-2 flex-col md:flex-row">
                <Input
                  label="Usable Area (sq/ft)"
                  name="usableArea"
                  value={formData.usableArea}
                  onChange={handleInputChange}
                />
                <Input
                  label="Total Area (sq/ft)"
                  name="totalArea"
                  value={formData.totalArea}
                  onChange={handleInputChange}
                />

              </div>
              <div className="">
                <Input
                  label="Floor Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                />
              </div>
              <div className="space-y-2">
                <div className="font-thin text-center mb-3">
                  Specify the Number of Features on this Floor
                </div>
                <div className="flex gap-2 mt-5 flex-col md:flex-row">
                  <Input
                    label={`Meeting Rooms ${formData.meetingRooms}`}
                    name="meetingRooms"
                    type="range"
                    min={0}
                    max={5}
                    color="#black"

                    className="text-black bg-black"
                    value={formData.meetingRooms}
                    onChange={handleInputChange}
                  />
                  <Input
                    label={`Rest Rooms ${formData.restrooms}`}
                    name="restrooms"
                    type="range"
                    min={0}
                    max={5}
                    value={formData.restrooms}
                    onChange={handleInputChange}
                  />
                  <Input
                    label={`Lounge ${formData.lounge}`}
                    name="lounge"
                    type="range"
                    min={0}
                    max={5}
                    value={formData.lounge}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="flex gap-2 flex-col md:flex-row">
                  <Input
                    label={`Cafeteria ${formData.cafeteria}`}
                    name="cafeteria"
                    type="range"
                    min={0}
                    max={5}
                    className="text-black"
                    value={formData.cafeteria}
                    onChange={handleInputChange}
                  />
                  <Input
                    label={`Prayer Rooms ${formData.prayerRoom}`}
                    name="prayerRoom"
                    type="range"
                    min={0}
                    max={5}
                    value={formData.prayerRoom}
                    onChange={handleInputChange}
                  />
                  <Input
                    label={`Game Area ${formData.gameroom}`}
                    name="gameroom"
                    type="range"
                    min={0}
                    max={5}
                    value={formData.gameroom}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="flex gap-2 flex-col md:flex-row">
                  <Input
                    label={`Reception ${formData.reception}`}
                    name="reception"
                    type="range"
                    min={0}
                    max={5}
                    className="text-black"
                    value={formData.reception}
                    onChange={handleInputChange}
                  />
                  <Input
                    label={`Public Area ${formData.publicArea}`}
                    name="publicArea"
                    type="range"
                    min={0}
                    max={5}
                    value={formData.publicArea}
                    onChange={handleInputChange}
                  />
                  <Input
                    label={`Smoking Area ${formData.smokingArea}`}
                    name="smokingArea"
                    type="range"
                    min={0}
                    max={5}
                    value={formData.smokingArea}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="flex gap-2 flex-col md:flex-row">
                  <Input
                    label={`Private Area ${formData.privateArea}`}
                    name="privateArea"
                    type="range"
                    min={0}
                    max={5}
                    className="text-black"
                    value={formData.privateArea}
                    onChange={handleInputChange}
                  />
                  <Input
                    label={`Guards ${formData.guard}`}
                    name="guard"
                    type="range"
                    min={0}
                    max={5}
                    value={formData.guard}
                    onChange={handleInputChange}
                  />
                  <Input
                    label={`Emergency Exits ${formData.emergencyExits}`}
                    name="emergencyExits"
                    type="range"
                    min={0}
                    max={5}
                    value={formData.emergencyExits}
                    onChange={handleInputChange}
                  />
                </div>

              </div>
              {/* <div className="">
                <span className="text-md font-thin text-gray-900">
                  Location
                </span>
              </div> */}
              {/* <div className="flex gap-4">
                <Input
                  label="Google Maps Link"
                  name="googleMapsLink"
                  value={formData.googleMapsLink}
                  onChange={handleInputChange}
                  icon={formData.googleMapsLink !== "" ? (googleMapsLinkStatus ? <Check size={12} /> : <ShieldAlert size={12} />) : <></>}
                />

                <Input
                  label="Longitude"
                  name="lng"
                  value={formData.lng}
                  onChange={handleInputChange}
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
                <Input
                  label="Latitude"
                  name="lat"
                  value={formData.lat}
                  onChange={handleInputChange}
                  type="number"
                  icon={isLoadingCoordinates ? <Spinner /> : <></>}
                />
              </div> */}

              <div className="">

                <Checkbox
                  name="fireSafetyStatus"
                  checked={formData.fireSafetyStatus}
                  className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                  label="Fire Safety"
                  onChange={handleCheckBoxChange}
                />
                <Checkbox
                  name="climateControl"
                  checked={formData.climateControl}
                  className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                  label="Climate Control"
                  onChange={handleCheckBoxChange}
                />
                <Checkbox
                  name="internetAvailability"
                  checked={formData.internetAvailability}
                  className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                  label="Internet"
                  onChange={handleCheckBoxChange}
                />
                <Checkbox
                  name="powerBackup"
                  checked={formData.powerBackup}
                  className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                  label="Power Backup"
                  onChange={handleCheckBoxChange}
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end space-x-2">
              <Button
                onClick={() => setModalOpen(false)}
                className="rounded-full "
                variant="text"
              >
                Cancel
              </Button>
              <Button type="submit" className="rounded-full">
                {isUpdating ? "Update" : "Add"}
              </Button>
            </div>
          </form>
        </ModalX>
      </div>

    </div>
  );
};

export default FloorPage;
